import { auth } from '../firebase';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

// ── Types ──────────────────────────────────────────────────────────
export interface NetworkPost {
  id: string;
  source_property_id: string;
  office_id: string;
  agent_id: string;
  city: string;
  neighborhood: string;
  property_type: string;
  price_min: number | null;
  price_max: number | null;
  size_min: number | null;
  size_max: number | null;
  rooms_min: number | null;
  rooms_max: number | null;
  floor_group: string | null;
  has_parking: boolean | null;
  has_elevator: boolean | null;
  has_saferoom: boolean | null;
  has_balcony: boolean | null;
  property_condition: string | null;
  entry_timing: string | null;
  public_description: string | null;
  is_exclusive: boolean;
  status: string;
  visibility: string;
  published_at: string | null;
  expires_at: string | null;
  approved_at: string | null;
  approved_by: string | null;
  review_note: string | null;
  created_at: string;
  updated_at: string;
}

export interface NetworkApiResult<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  code?: string;
}

// ── Auth helper ────────────────────────────────────────────────────
async function getAuthToken(): Promise<string> {
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error('לא מחובר');
  return token;
}

// ── Generic edge-function caller (POST) ─────────────────────────────
export async function callNetworkFunction<T = unknown>(
  functionName: string,
  body: Record<string, unknown>
): Promise<NetworkApiResult<T>> {
  const token = await getAuthToken();
  const res = await fetch(`${SUPABASE_URL}/functions/v1/${functionName}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  return res.json() as Promise<NetworkApiResult<T>>;
}

// ── Read: load MY posts only (user-scoped) ───────────────────────────
export async function getMyNetworkPosts(): Promise<NetworkPost[]> {
  const token = await getAuthToken();
  const res = await fetch(`${SUPABASE_URL}/functions/v1/network-get-my-property-posts`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const json = await res.json() as NetworkApiResult<{ posts: NetworkPost[] }>;
  if (!json.success) {
    throw new Error(json.message || 'שגיאה בטעינת פרסומי הרשת');
  }
  return json.data?.posts ?? [];
}

// ── Read: load ALL office posts (office-scoped, includes other agents) ──
export interface OfficePostsResponse {
  posts: NetworkPost[];
  agentNames: Record<string, string>;
  myUid: string;
}

export async function getOfficeNetworkPosts(): Promise<OfficePostsResponse> {
  const token = await getAuthToken();
  const res = await fetch(`${SUPABASE_URL}/functions/v1/network-get-office-property-posts`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const json = await res.json() as NetworkApiResult<OfficePostsResponse>;
  if (!json.success) {
    throw new Error(json.message || 'שגיאה בטעינת פרסומי המשרד');
  }
  return {
    posts: json.data?.posts ?? [],
    agentNames: json.data?.agentNames ?? {},
    myUid: json.data?.myUid ?? '',
  };
}

// ── Write: create a draft from a Firestore property ─────────────────
// Sends sourcePropertyId (= formId) + networkData object.
// No street, house number, owner, office ID, or agent ID sent from frontend.
export interface NetworkDraftData {
  priceMin: number | null;
  priceMax: number | null;
  sizeMin: number | null;
  sizeMax: number | null;
  roomsMin: number | null;
  roomsMax: number | null;
  floorGroup: string | null;
  entryTiming: string | null;
  publicDescription: string | null;
  isExclusive: boolean;
}

export async function createNetworkDraft(
  sourcePropertyId: string,
  networkData: NetworkDraftData
): Promise<NetworkApiResult<{ post: NetworkPost }>> {
  return callNetworkFunction<{ post: NetworkPost }>('network-create-property-draft', {
    sourcePropertyId,
    networkData,
  });
}

// ── Write: submit a draft for approval/publishing ───────────────────
export async function submitPropertyPost(
  postId: string
): Promise<NetworkApiResult<{ post: NetworkPost }>> {
  return callNetworkFunction<{ post: NetworkPost }>('network-submit-property-post', {
    postId,
  });
}

// ── Write: change post status (pause/resume/close/remove) ───────────
export async function changePostStatus(
  postId: string,
  action: 'pause' | 'resume' | 'close' | 'remove'
): Promise<NetworkApiResult<{ post: NetworkPost }>> {
  return callNetworkFunction<{ post: NetworkPost }>('network-change-property-post-status', {
    postId,
    action,
  });
}

// ── Read: resolve effective network permissions for current user ──
// Uses network-get-my-permissions (GET with Firebase ID token).
// Returns officeRole, networkRole, and derived canReviewNetworkPosts.
export interface NetworkPermissions {
  officeRole: string;
  networkRole: string;
  canPublishPosts: boolean;
  canApproveCollaboration: boolean;
  canReviewNetworkPosts: boolean;
}

export async function getMyNetworkPermissions(): Promise<NetworkPermissions | null> {
  const token = await getAuthToken();
  const res = await fetch(`${SUPABASE_URL}/functions/v1/network-get-my-permissions`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await res.json() as NetworkApiResult<NetworkPermissions>;
  if (!json.success || !json.data) return null;
  return json.data;
}

// ── Read: public marketplace posts (anonymous, privacy-safe) ────────
// Uses network-get-public-property-posts (POST with Firebase ID token).
// Only authenticated RV network members may browse. The edge function
// queries the network_public_property_posts view and returns only
// privacy-safe fields (no office/agent/address/source identifiers).
export interface PublicNetworkPropertyPost {
  id: string;
  city: string;
  neighborhood: string | null;
  property_type: string;
  price_min: number | null;
  price_max: number | null;
  size_min: number | null;
  size_max: number | null;
  rooms_min: number | null;
  rooms_max: number | null;
  floor_group: string | null;
  has_parking: boolean | null;
  has_elevator: boolean | null;
  has_saferoom: boolean | null;
  has_balcony: boolean | null;
  property_condition: string | null;
  entry_timing: string | null;
  public_description: string | null;
  is_exclusive: boolean;
  is_own_post: boolean;
  published_at: string | null;
  expires_at: string | null;
}

export interface MarketplaceFilters {
  city?: string;
  neighborhood?: string;
  propertyType?: string;
  priceMin?: number;
  priceMax?: number;
  roomsMin?: number;
  roomsMax?: number;
  sizeMin?: number;
  sizeMax?: number;
  hasParking?: boolean;
  hasSaferoom?: boolean;
  hasBalcony?: boolean;
  hasElevator?: boolean;
  page?: number;
  pageSize?: number;
}

export interface MarketplaceResponse {
  items: PublicNetworkPropertyPost[];
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
}

export async function getPublicNetworkPropertyPosts(
  filters: MarketplaceFilters = {}
): Promise<MarketplaceResponse> {
  const token = await getAuthToken();
  const res = await fetch(`${SUPABASE_URL}/functions/v1/network-get-public-property-posts`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(filters),
  });
  const json = await res.json() as NetworkApiResult<MarketplaceResponse> & MarketplaceResponse;
  if (!json.success) {
    throw new Error(json.message || 'לא ניתן לטעון כרגע את הנכסים ברשת');
  }
  return {
    items: json.items ?? [],
    page: json.page ?? 1,
    pageSize: json.pageSize ?? 20,
    total: json.total ?? 0,
    hasMore: !!json.hasMore,
  };
}

// ── Write: review (approve/reject) a pending property post ──────────
export async function reviewPropertyPost(
  postId: string,
  decision: 'approve' | 'reject',
  reason?: string
): Promise<NetworkApiResult<{ post: NetworkPost }>> {
  const body: Record<string, unknown> = { postId, decision };
  if (reason) body.reason = reason;
  return callNetworkFunction<{ post: NetworkPost }>('network-review-property-post', body);
}

// ── Write: update post fields ──────────────────────────────────────
export async function updateNetworkPost(
  postId: string,
  updates: Partial<Pick<NetworkPost,
    'price_min' | 'price_max' | 'size_min' | 'size_max' |
    'rooms_min' | 'rooms_max' | 'floor_group' | 'entry_timing' | 'public_description'
  >>
): Promise<NetworkApiResult<{ post: NetworkPost }>> {
  return callNetworkFunction<{ post: NetworkPost }>('network-update-property-post', {
    postId,
    updates: { ...updates, isExclusive: true },
  });
}

// ════════════════════════════════════════════════════════════════════
// Collaboration Requests (Sprint 4B)
// ════════════════════════════════════════════════════════════════════

export interface BuyerFitInput {
  budgetMin?: number | null;
  budgetMax: number;
  buyerPurpose: 'residence' | 'investment' | 'other';
  buyerReadiness: 'hot' | 'active' | 'initial';
  financingStatus?: 'cash' | 'mortgage_approved' | 'mortgage_in_progress' | 'unknown' | null;
  currentPropertyStatus?: 'not_relevant' | 'already_sold' | 'listed_for_sale' | 'not_yet_listed' | 'unknown' | null;
  message: string;
}

export interface CollaborationContactProjection {
  office_name: string | null;
  office_logo: string | null;
  office_phone: string | null;
  office_email: string | null;
  agent_name: string | null;
  agent_phone: string | null;
  agent_email: string | null;
}

export interface CollaborationRequestProperty {
  id: string;
  city: string;
  neighborhood: string | null;
  property_type: string;
  price_min: number | null;
  price_max: number | null;
  size_min: number | null;
  size_max: number | null;
  rooms_min: number | null;
  rooms_max: number | null;
  floor_group: string | null;
  has_parking: boolean | null;
  has_elevator: boolean | null;
  has_saferoom: boolean | null;
  has_balcony: boolean | null;
  property_condition: string | null;
  entry_timing: string | null;
  public_description: string | null;
  is_exclusive: boolean;
}

export interface CollaborationRequest {
  id: string;
  property_post_id: string;
  direction: 'sent' | 'received';
  status: 'pending' | 'more_info_requested' | 'approved' | 'rejected' | 'cancelled' | 'closed';
  requesting_agent_id?: string | null;
  receiving_agent_id?: string | null;
  budget_min: number | null;
  budget_max: number;
  buyer_purpose: string;
  buyer_readiness: string;
  financing_status: string | null;
  current_property_status: string | null;
  message: string | null;
  review_note: string | null;
  more_info_requested_at: string | null;
  created_at: string;
  updated_at: string;
  approved_at: string | null;
  rejected_at: string | null;
  cancelled_at: string | null;
  property_post: CollaborationRequestProperty | null;
  contact?: CollaborationContactProjection | null;
}

export interface CollaborationRequestFilters {
  direction?: 'sent' | 'received' | 'all';
  status?: string;
  page?: number;
  pageSize?: number;
}

export interface PaginatedCollaborationResponse {
  items: CollaborationRequest[];
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
}

// ── Create a collaboration request ──────────────────────────────────
export async function createCollaborationRequest(
  propertyPostId: string,
  buyerFit: BuyerFitInput
): Promise<NetworkApiResult<{ request: CollaborationRequest }>> {
  return callNetworkFunction<{ request: CollaborationRequest }>(
    'network-create-collaboration-request',
    { propertyPostId, buyerFit }
  );
}

// ── Get my collaboration requests (sent + received) ──────────────────
export async function getMyCollaborationRequests(
  filters: CollaborationRequestFilters = {}
): Promise<PaginatedCollaborationResponse> {
  const token = await getAuthToken();
  const res = await fetch(`${SUPABASE_URL}/functions/v1/network-get-my-collaboration-requests`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(filters),
  });
  const json = await res.json() as NetworkApiResult<PaginatedCollaborationResponse> & PaginatedCollaborationResponse;
  if (!json.success) {
    throw new Error(json.message || 'לא ניתן לטעון כרגע את בקשות שיתוף הפעולה');
  }
  return {
    items: json.items ?? [],
    page: json.page ?? 1,
    pageSize: json.pageSize ?? 20,
    total: json.total ?? 0,
    hasMore: !!json.hasMore,
  };
}

// ── Review a collaboration request (approve/reject/request_more_info) ─
export async function reviewCollaborationRequest(
  requestId: string,
  action: 'approve' | 'reject' | 'request_more_info',
  note?: string
): Promise<NetworkApiResult<{ request: CollaborationRequest }>> {
  const body: Record<string, unknown> = { requestId, action };
  if (note) body.note = note;
  return callNetworkFunction<{ request: CollaborationRequest }>(
    'network-review-collaboration-request',
    body
  );
}

// ── Update buyer-fit info on a request (requesting office) ───────────
export async function updateCollaborationRequest(
  requestId: string,
  updates: Partial<BuyerFitInput>
): Promise<NetworkApiResult<{ request: CollaborationRequest }>> {
  return callNetworkFunction<{ request: CollaborationRequest }>(
    'network-update-collaboration-request',
    { requestId, updates }
  );
}

// ── Cancel or close a collaboration request ──────────────────────────
export async function changeCollaborationRequestStatus(
  requestId: string,
  action: 'cancel' | 'close'
): Promise<NetworkApiResult<{ request: CollaborationRequest }>> {
  return callNetworkFunction<{ request: CollaborationRequest }>(
    'network-change-collaboration-request-status',
    { requestId, action }
  );
}
